import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/components/layout/CreateBookingModal.css';

const CreateBookingModal = ({
  recipientName,
  onClose,
  onSubmit,
  initialBooking = null,
}) => {
  const initialDate = initialBooking?.date
    ? new Date(initialBooking.date)
    : null;

  const initialSchedule = initialBooking?.paymentSchedule || [];
  const depositRow = initialSchedule.find((row) => row.type === 'deposit');
  const balanceRow = initialSchedule.find((row) => row.type === 'balance');
  const initialPriceCents = Number(initialBooking?.price_cents || 0);

  const initialDepositPercentage =
    depositRow && initialPriceCents
      ? Math.round((Number(depositRow.amount_cents) / initialPriceCents) * 100)
      : 20;

  const initialBalanceDaysBefore = useMemo(() => {
    if (!balanceRow?.dueAt || !initialDate) {
      return 14;
    }

    const due = new Date(balanceRow.dueAt);
    const diffMs = initialDate.getTime() - due.getTime();

    return Math.max(0, Math.round(diffMs / 86400000));
  }, [balanceRow?.dueAt, initialDate]);

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [allDay, setAllDay] = useState(Boolean(initialBooking?.allDay));
  const [startTime, setStartTime] = useState(initialBooking?.start || '');
  const [endTime, setEndTime] = useState(initialBooking?.end || '');
  const [address, setAddress] = useState(initialBooking?.address || '');
  const [eventName, setEventName] = useState(initialBooking?.eventName || '');
  const [shootType, setShootType] = useState(initialBooking?.shootType || '');
  const [price, setPrice] = useState(initialBooking?.price || '');
  const [phone, setPhone] = useState(initialBooking?.contactPhone || '');
  const [paymentType, setPaymentType] = useState(
    depositRow ? 'deposit' : 'full_payment'
  );
  const [depositPercentage, setDepositPercentage] = useState(
    initialDepositPercentage
  );
  const [balanceDueDaysBefore, setBalanceDueDaysBefore] = useState(
    initialBalanceDaysBefore
  );

  const isScheduleValid =
    selectedDate && (allDay || (startTime && endTime));

  const isPaymentTermsValid =
    paymentType === 'full_payment' ||
    (
      Number(depositPercentage) > 0 &&
      Number(depositPercentage) < 100 &&
      Number(balanceDueDaysBefore) >= 0
    );

  const isFormValid =
    isScheduleValid &&
    address &&
    eventName &&
    shootType &&
    Number(price) > 0 &&
    isPaymentTermsValid;

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!isFormValid) {
      return;
    }

    onSubmit({
      date: selectedDate,
      allDay,
      startTime: allDay ? null : startTime,
      endTime: allDay ? null : endTime,
      address,
      eventName,
      shootType,
      price,
      contactPhone: phone || null,
      paymentTerms:
        paymentType === 'deposit'
          ? {
              type: 'deposit',
              deposit_percentage: Number(depositPercentage),
              balance_due_days_before: Number(balanceDueDaysBefore),
            }
          : {
              type: 'full_payment',
            },
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-scroll">
          <h2>{initialBooking ? 'Revise booking' : 'Create booking'}</h2>

          <form onSubmit={handleSubmit} className="booking-form">
          <div className="form-group">
            <label>Recipient</label>
            <input type="text" value={recipientName} readOnly />
          </div>

          <div className="form-group">
            <label>Date</label>
            <DatePicker
              selected={selectedDate}
              onChange={setSelectedDate}
              placeholderText="Select a date"
              dateFormat="yyyy/MM/dd"
            />

            <div className="inline-field">
              <input
                type="checkbox"
                id="allDay"
                checked={allDay}
                onChange={() => setAllDay((value) => !value)}
              />
              <label htmlFor="allDay">All-day</label>
            </div>
          </div>

          {!allDay && (
            <div className="form-group time-range">
              <label>Start time</label>
              <input
                type="time"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
              />

              <label>End time</label>
              <input
                type="time"
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
              />
            </div>
          )}

          <div className="form-group">
            <label>Location / Address</label>
            <input
              type="text"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              placeholder="Where is the shoot?"
            />
          </div>

          <div className="form-group">
            <label>Event / Project name</label>
            <input
              type="text"
              value={eventName}
              onChange={(event) => setEventName(event.target.value)}
              placeholder="e.g. Alice & Bob Wedding"
            />
          </div>

          <div className="form-group">
            <label>Shoot type</label>
            <select
              value={shootType}
              onChange={(event) => setShootType(event.target.value)}
            >
              <option value="">Select…</option>
              <option value="wedding">Wedding</option>
              <option value="portrait">Portrait</option>
              <option value="real-estate">Real estate</option>
              <option value="event">Event</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>Total price (GBP)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              placeholder="Agreed price"
            />
          </div>

          <fieldset className="payment-terms-fieldset">
            <legend>Payment terms</legend>

            <label className="payment-option">
              <input
                type="radio"
                name="paymentType"
                value="full_payment"
                checked={paymentType === 'full_payment'}
                onChange={() => setPaymentType('full_payment')}
              />
              Full payment on acceptance
            </label>

            <label className="payment-option">
              <input
                type="radio"
                name="paymentType"
                value="deposit"
                checked={paymentType === 'deposit'}
                onChange={() => setPaymentType('deposit')}
              />
              Deposit now, balance later
            </label>

            {paymentType === 'deposit' && (
              <div className="deposit-fields">
                <label>
                  Deposit percentage
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={depositPercentage}
                    onChange={(event) =>
                      setDepositPercentage(event.target.value)
                    }
                  />
                </label>

                <label>
                  Balance due days before shoot
                  <input
                    type="number"
                    min="0"
                    max="365"
                    value={balanceDueDaysBefore}
                    onChange={(event) =>
                      setBalanceDueDaysBefore(event.target.value)
                    }
                  />
                </label>
              </div>
            )}
          </fieldset>

          <div className="form-group">
            <label>Client contact phone (optional)</label>
            <input
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="+44 …"
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="secondary-btn">
              Cancel
            </button>

            <button type="submit" disabled={!isFormValid} className="primary-btn">
              {initialBooking ? 'Save revision' : 'Send booking proposal'}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateBookingModal;
